import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import MenuBar from "@/components/MenuBar";
import { PlusCircle, Trash2, Menu, AlertTriangle, Info } from "lucide-react";
import AddCustomRateModal from "@/components/AddCustomRateModal";
import EmployeeDeductions from "@/components/earnings/interfaces";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import {
  CustomRate,
  EmployeeEarning,
  EmployeeEarningsResponse,
} from "@/types/earnings";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";
import SuperUserEmployerSelector from "@/components/common/SuperUserEmployerSelector";
import EmployeeSelector from "@/components/common/EmployeeSelector";
import { useEmployees } from "@/contexts/EmployeeContext";

interface RateTypesResponse {
  rateTypes: string[];
}

const Earnings: React.FC = () => {
  const { user } = useAuth();
  const [availableRateTypes, setAvailableRateTypes] = useState<string[]>([]);
  const [employeeEarnings, setEmployeeEarnings] = useState<EmployeeEarning[]>(
    [],
  );
  const [employeeDeductions, setEmployeeDeductions] =
    useState<EmployeeDeductions | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEmployeeId, setCurrentEmployeeId] = useState<number | null>(
    null,
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { selectedEmployeeId } = useEmployees();

  // Superuser specific state
  const isSuperUserAccount = isSuperUser(user);
  const [selectedUserId, setSelectedUserId] = useState<string>("current");
  const [hasSelectedEmployer, setHasSelectedEmployer] = useState(false);

  useEffect(() => {
    if (isSuperUserAccount && selectedUserId !== "current") {
      setHasSelectedEmployer(true);
    }
  }, [selectedUserId, isSuperUserAccount]);

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
    const newUrl = new URL(window.location.href);
    if (userId !== "current") {
      newUrl.searchParams.set("userId", userId);
    } else {
      newUrl.searchParams.delete("userId");
    }
    window.history.replaceState({}, "", newUrl.toString());
  };

  useEffect(() => {
    if (!isSuperUserAccount || hasSelectedEmployer) {
      fetchData();
    }
  }, [selectedUserId, isSuperUserAccount, hasSelectedEmployer]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        fetchEmployees(),
        fetchAvailableRateTypes(),
        fetchEmployeeDeductions(),
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEmployeeDeductions = async () => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const response = await ApiService.request<EmployeeDeductions>(
        EndpointKeys.FETCH_EMPLOYEE_DEDUCTIONS,
        {
          queryParams,
        },
      );
      setEmployeeDeductions(response);
    } catch (error) {
      console.error("Error fetching employee deductions:", error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const response = await ApiService.request<EmployeeEarningsResponse>(
        EndpointKeys.FETCH_EMPLOYEE_EARNINGS,
        {
          queryParams,
        },
      );
      // If a specific employee is selected via context, filter to that employee
      const filtered =
        selectedEmployeeId != null
          ? response.employees.filter(
              (e) => e.employee.id === selectedEmployeeId,
            )
          : response.employees;
      setEmployeeEarnings(filtered);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const fetchAvailableRateTypes = async () => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const response = await ApiService.request<RateTypesResponse>(
        EndpointKeys.FETCH_AVAILABLE_RATE_TYPES,
        {
          queryParams,
        },
      );
      setAvailableRateTypes(response.rateTypes);
    } catch (error) {
      console.error("Error fetching available rate types:", error);
    }
  };

  const handleAddCustomRate = (employeeId: number) => {
    setCurrentEmployeeId(employeeId);
    setIsModalOpen(true);
  };

  const handleRemoveCustomRate = async (employeeId: number, rateId: number) => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      await ApiService.request<void>(EndpointKeys.REMOVE_CUSTOM_RATE, {
        method: "POST",
        body: { employeeId, rateId },
        queryParams,
      });

      setEmployeeEarnings((prev) =>
        prev.map((emp) => {
          if (emp.employee.id === employeeId) {
            return {
              ...emp,
              customRates: emp.customRates.filter((rate) => rate.id !== rateId),
            };
          }
          return emp;
        }),
      );
    } catch (error) {
      console.error("Error removing custom rate:", error);
    }
  };

  const handleAddRate = async (
    rate: Omit<CustomRate, "id" | "customRateVersion">,
  ) => {
    if (currentEmployeeId === null) return;

    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const newRate = await ApiService.request<CustomRate>(
        EndpointKeys.ADD_CUSTOM_RATE,
        {
          method: "POST",
          body: {
            employeeId: currentEmployeeId,
            ...rate,
          },
          queryParams,
        },
      );

      setEmployeeEarnings((prev) =>
        prev.map((emp) => {
          if (emp.employee.id === currentEmployeeId) {
            return {
              ...emp,
              customRates: [...emp.customRates, newRate],
            };
          }
          return emp;
        }),
      );
    } catch (error) {
      console.error("Error adding custom rate:", error);
    }
  };

  if (isLoading && (!isSuperUserAccount || hasSelectedEmployer)) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const MainContent = () => {
    // If superuser without selected employer, show selection prompt
    if (isSuperUserAccount && !hasSelectedEmployer) {
      return (
        <CardContent>
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="mb-4 rounded-full bg-yellow-100 p-4">
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            </div>
            <h2 className="mb-2 text-xl font-semibold">
              Please Select an Employer
            </h2>
            <p className="max-w-md text-gray-600">
              As a superuser, you need to select an employer from the dropdown
              above before viewing or managing earnings data.
            </p>
          </div>
        </CardContent>
      );
    }

    return (
      <CardContent>
        <Alert className="mb-4">
          <Info className="h-4 w-4 text-primary" />
          <AlertTitle>Custom Earnings Management</AlertTitle>
          <AlertDescription>
            This is where you can manage all your custom rates outside of your
            fixed salary. You can define as many custom earnings as you need,
            such as overtime, bonuses, or other special payment arrangements.
            Each custom rate can be configured with different multipliers or
            fixed amounts to accurately reflect your specific compensation
            agreements.
          </AlertDescription>
        </Alert>

        <div className="space-y-6">
          {employeeEarnings.map((employee) => (
            <Card key={employee.employee.id}>
              <CardHeader>
                <h3 className="text-xl font-semibold">
                  {`${employee.employee.firstName} ${employee.employee.lastName}`}
                </h3>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto">
                  <div className="min-w-[600px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Rate Name</TableHead>
                          <TableHead>Rate Type</TableHead>
                          <TableHead>Unit Type</TableHead>
                          <TableHead>Multiplier</TableHead>
                          <TableHead>Fixed Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {employee.customRates.map((rate) => (
                          <TableRow key={rate.id}>
                            <TableCell>{rate.name}</TableCell>
                            <TableCell>{rate.rateType}</TableCell>
                            <TableCell>{rate.unitType}</TableCell>
                            <TableCell>{rate.multiplier || "N/A"}</TableCell>
                            <TableCell>{rate.fixedAmount || "N/A"}</TableCell>
                          </TableRow>
                        ))}
                        {employee.customRates.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              className="text-center text-muted-foreground"
                            >
                              No custom rates configured
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                <Button
                  className="mt-4"
                  onClick={() => handleAddCustomRate(employee.employee.id)}
                >
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Add Custom Rate
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    );
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0 overflow-auto">
        <div className="p-4 md:p-6 max-w-7xl space-y-6">
          <Card>
            <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0">
              <div className="space-y-1.5">
                <h2 className="text-2xl font-bold">Earnings Configuration</h2>
                {isSuperUserAccount && (
                  <p className="text-sm text-muted-foreground">
                    {selectedUserId !== "current"
                      ? "Viewing and managing earnings data for selected user"
                      : "Viewing your own earnings data"}
                  </p>
                )}
              </div>

              {/* Add the SuperUser selector in desktop view */}
              {isSuperUserAccount && (
                <div className="hidden md:block">
                  <SuperUserEmployerSelector
                    selectedUserId={selectedUserId}
                    onUserChange={handleUserChange}
                  />
                </div>
              )}
            </CardHeader>

            {/* Employee selector */}
            <div className="px-6 mb-4">
              <EmployeeSelector label="Filter by employee" placeholder="All employees" />
            </div>

            {/* User Selector - ONLY for mobile, appears at top of content when menu closed */}
            {isSuperUserAccount && (
              <div className="md:hidden px-6 mb-4">
                <SuperUserEmployerSelector
                  selectedUserId={selectedUserId}
                  onUserChange={handleUserChange}
                />
              </div>
            )}

            <MainContent />
          </Card>
        </div>
      </div>

      <AddCustomRateModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddRate}
        availableRateTypes={availableRateTypes}
        availableDependentDeductions={employeeDeductions}
      />
    </div>
  );
};

export default Earnings;
